'use client';

import { useState, FormEvent } from 'react';
import Link from 'next/link';
import { DATA_DELETION_MESSAGES } from '@shared/constants';
import { Logger } from '@shared/logger';
import { FetchApiClient } from '@shared/httpService';
import { tryCatchAsync } from '@shared/utils';

interface DeleteDataPageProps {
  privacyPolicyUrl?: string;
}

/**
 * @group Public
 * @summary Data deletion request page component
 * 
 * <blockquote>
 * Renders the data deletion request form. Users enter their email address
 * to request deletion of all associated data. Shows success message regardless
 * of email validity (enumeration prevention).
 * </blockquote>
 */
export function DeleteDataPage({ 
  privacyPolicyUrl = '/privacy-policy' 
}: DeleteDataPageProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const wrappedResult = await tryCatchAsync(async () => {
      const fetchApi = new FetchApiClient();
      const result = await fetchApi.post('/api/data/delete-request', {
        email: email.trim(),
      });

      if (result.isErr) {
        const errorData = result.getFail();
        Logger.error('Deletion request failed', { 
          resource: 'data.deletion',
          errorCode: errorData.errorCode,
        }, new Error(errorData.errorMessage));
        return { success: false };
      }

      const response = result.getSuccess();
      if (response.ok) {
        return { success: true };
      }
      
      // Should not happen as API always returns success
      const data = response.data as { message?: string };
      return { success: false, message: data.message };
    });

    wrappedResult.match({
      ok: (data) => {
        if (data.success) {
          setIsSubmitted(true);
        } else {
          setError(data.message || DATA_DELETION_MESSAGES.ERRORS.REQUEST_FAILED);
        }
      },
      err: (error) => {
        Logger.error('Deletion request failed', { 
          resource: 'data.deletion',
        }, new Error(error.errorMessage));
        setError(DATA_DELETION_MESSAGES.ERRORS.REQUEST_FAILED);
      }
    });

    setIsLoading(false);
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Request Submitted
          </h2>
          <p className="text-gray-600 mb-6">
            {DATA_DELETION_MESSAGES.SUCCESS.REQUEST_SUBMITTED}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Check your email for a confirmation link. The link will expire in 24 hours.
          </p>
          <Link 
            href="/"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
          {DATA_DELETION_MESSAGES.UI.REQUEST_PAGE_TITLE}
        </h1>
        <p className="text-gray-600 text-center mb-6">
          {DATA_DELETION_MESSAGES.UI.REQUEST_PAGE_DESCRIPTION}
        </p>

        {/* Warning Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                <strong>Warning:</strong> {DATA_DELETION_MESSAGES.UI.IRREVERSIBLE_WARNING}
              </p>
              <p className="text-sm text-amber-600 mt-1">
                {DATA_DELETION_MESSAGES.UI.DATA_DELETED_LIST}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email address"
              disabled={isLoading}
              aria-describedby="email-description"
            />
            <p id="email-description" className="mt-1 text-sm text-gray-500">
              Enter the email address associated with your account.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3" role="alert">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? DATA_DELETION_MESSAGES.UI.LOADING : DATA_DELETION_MESSAGES.UI.SUBMIT_BUTTON}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <Link 
            href={privacyPolicyUrl}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            View our Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

